/* eslint-disable react-hooks/rules-of-hooks */
import { API } from "@swivl-mono/api-helper";
import { AppInterface, AppType, LocationVarKey, TwilioSetupInterface, TwilioSubaccountsResponse } from "@swivl-mono/types";
import _ from "lodash";
import React from "react";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";

interface StoreInterface {
  apps?: AppInterface[];
  twilioSetup?: TwilioSetupInterface;
  twilioSubaccounts?: TwilioSubaccountsResponse;
  isLoadingSubaccounts?: boolean;
  isConfiguringSubaccount?: boolean;
}
const BlankState: StoreInterface = {
  apps: undefined,
  twilioSubaccounts: undefined,
  isLoadingSubaccounts: false,
  isConfiguringSubaccount: false
};

export default class AppsModel {
  // static Store = new Store<StoreInterface>({
  //   apps: undefined,
  // });
  // static Store = {
  //   useState : create(set => ({  apps: undefined }))
  //   update:AppsModel.useState.set
  // }

  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  static setState = AppsModel.useState.setState;
  static get state() {
    return AppsModel.useState.getState();
  }
  static reset() {
    AppsModel.setState({ ...BlankState });
  }

  static useGetApp(type: AppType) {
    const { apps } = AppsModel.useState((s) => s);
    const [app, setApp] = React.useState<AppInterface | undefined>(undefined);
    React.useEffect(() => {
      if (apps) {
        setApp(apps.find((a) => a.type === type));
      }
    }, [apps, type]);
    return app;
  }

  static async load(forceRefresh) {
    if (AppsModel.state.apps && AppsModel.state.apps.length > 0 && !forceRefresh) {
      return;
    }
    const apps = await API.Apps.getApps(Member.orgId!);

    AppsModel.setState({ apps: apps.filter((app) => app.active) });
    await AppsModel.loadTwilioSetup();
  }

  static async loadTwilioSetup() {
    const twilioSetup = await API.TwilioSetup.getCurrentTwilioSetup(Member.orgId!);
    AppsModel.setState({ ...AppsModel.state, twilioSetup: twilioSetup });
    return twilioSetup;
  }

  static async loadApp(appType) {
    const appsByType = await API.Apps.getAppsByType(Member.orgId!, appType);
    let apps = AppsModel.state.apps ? [...AppsModel.state.apps] : [];
    for (var i = 0; i < appsByType.length; i++) {
      const existingAppIndex = apps.findIndex((a) => a.id === appsByType[i].id);
      if (existingAppIndex !== -1) {
        apps[existingAppIndex] = appsByType[i];
      } else {
        apps.push(appsByType[i]);
      }
    }
    AppsModel.setState({ apps: apps.filter((app) => app.active) });
  }
  static async getAppByType(appType: AppType) {
    if (AppsModel.state.apps) {
      for (var i = 0; i < AppsModel.state.apps.length; i++) {
        if (AppsModel.state.apps[i].type === appType) {
          return AppsModel.state.apps[i];
        }
      }
    }
  }

  static async createApp(type: AppType) {
    const app = await API.Apps.createApp(Member.orgId!, type);
    let apps = AppsModel.state.apps ? [...AppsModel.state.apps] : [];
    apps.push(app);
    const twilioSetup = await API.TwilioSetup.getCurrentTwilioSetup(Member.orgId!);
    AppsModel.setState({ ...AppsModel.state, apps: apps, twilioSetup: twilioSetup });
    return app;
  }

  static async setupTwilio(): Promise<AppInterface | false> {
    const twilioSetup = await API.TwilioSetup.getCurrentTwilioSetup(Member.orgId!);
    AppsModel.setState({ ...AppsModel.state, twilioSetup: twilioSetup });

    AppsModel.load(true);
    const apps = AppsModel.state.apps;
    if (apps && apps.filter((a) => a.type === AppType.TWILIO && a.active).length > 0) {
      return false;
    }
    const app = await API.TwilioSetup.setupTwilio(Member.orgId!);
    if (app) {
      return app;
    }
    return false;
  }

  static async update(app: AppInterface, shouldSave?: boolean) {
    let apps = AppsModel.state.apps ? [...AppsModel.state.apps] : [];

    for (var i = 0; i < apps.length; i++) {
      if (apps[i].id === app.id) {
        apps[i] = app;
        AppsModel.setState({ apps: apps });

        if (shouldSave) {
          await API.Apps.updateApp(Member.orgId!, app);
        }
        break;
      }
    }
  }
  static enabledApps(): AppType[] {
    if (AppsModel.state.apps) {
      return AppsModel.state.apps.filter((a) => a.active).map((a) => a.type);
    }
    return [];
  }
  static hasApp(type) {
    if (AppsModel.state.apps) {
      for (var i = 0; i < AppsModel.state.apps.length; i++) {
        if (AppsModel.state.apps[i].type === type && AppsModel.state.apps[i].active) {
          return true;
        }
      }
    }

    return null;
  }
  // static getApp(type) {

  //   var apps = AppsModel.state.apps;
  //   if (!apps) {
  //     return null;
  //   }
  //   for (var i = 0; i < apps.length; i++) {
  //     if (apps[i].get('type') && apps[i].get('type') == type) {
  //       return apps[i];
  //     }
  //   }
  //   return null;
  // }
  static async loadTwilioSubaccounts(): Promise<TwilioSubaccountsResponse | undefined> {
    if (!Member.orgId) return undefined;

    AppsModel.setState({ ...AppsModel.state, isLoadingSubaccounts: true });
    try {
      const subaccounts = await API.TwilioSetup.listSubaccounts(Member.orgId);
      AppsModel.setState({ ...AppsModel.state, twilioSubaccounts: subaccounts, isLoadingSubaccounts: false });
      return subaccounts;
    } catch (error) {
      console.error("Failed to load Twilio subaccounts:", error);
      AppsModel.setState({ ...AppsModel.state, isLoadingSubaccounts: false });
      return undefined;
    }
  }

  /**
   * Link a subaccount to the org. On success the result also says what happened
   * to the org's CC v3 softphone key, the Subaccount pair (ENG-3719 / ENG-3747):
   * `kept` when it already lived inside the subaccount, `copied` when the
   * hand-pasted Outbound key did and was copied over, `created` when one was
   * made there. The Outbound pair is the legacy softphone's and is never written.
   */
  static async configureSubaccount(subaccountSid: string): Promise<{ success: boolean; softphoneKey?: "kept" | "copied" | "created" }> {
    if (!Member.orgId) return { success: false };

    AppsModel.setState({ ...AppsModel.state, isConfiguringSubaccount: true });
    try {
      const result = await API.TwilioSetup.configureSubaccount(Member.orgId, subaccountSid);
      if (result.success) {
        // Reload the Twilio setup to get the updated values
        await AppsModel.loadTwilioSetup();
        // Reload subaccounts to update the current selection
        await AppsModel.loadTwilioSubaccounts();
      }
      AppsModel.setState({ ...AppsModel.state, isConfiguringSubaccount: false });
      return { success: result.success, softphoneKey: result.softphoneKey };
    } catch (error) {
      console.error("Failed to configure Twilio subaccount:", error);
      AppsModel.setState({ ...AppsModel.state, isConfiguringSubaccount: false });
      return { success: false };
    }
  }

  static deactivateApp(app: AppInterface) {
    app.active = false;
    AppsModel.update(app, true);
  }
  //   var apps = [...AppsModel.state.apps];
  //   if (!apps) {
  //     return;
  //   }
  //   for (var i = 0; i < apps.length; i++) {
  //     if (apps[i].id === app.id) {
  //       apps.splice(i, 1);
  //       if (BotModel.state.bot && app.get('bot') && BotModel.state.bot.id == app.get('bot').id && BotModel.state.bot.get('apps')) {
  //         var bot = BotModel.state.bot;
  //         var botApps = bot.get('apps');
  //         var newBotApps = [];
  //         for (var i = 0; i < botApps.length; i++) {
  //           if (botApps[i] !== app.get('type')) {
  //             newBotApps.push(botApps[i]);
  //           }
  //         }
  //         bot.set('apps', newBotApps);
  //         bot.save();
  //         AppsModel.setState({ apps: apps });
  //       } else {
  //         AppsModel.setState({ apps: apps });
  //       }
  //       app.set('active', false);
  //       app.save();
  //       break;
  //     }
  //   }
  // }
}
